import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faRedo,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import '../scss/TemperatureMonitoring.scss';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TemperatureMonitoring = () => {
  const navigate = useNavigate();
  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
  // State for filters
  const [selectedCrane, setSelectedCrane] = useState('');
  const [cranes, setCranes] = useState([]);
  
  // State for data
  const [loading, setLoading] = useState(false);
  const [showData, setShowData] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState({
    timestamps: [],
    hoistTemperature: [],
    ctTemperature: [],
    ltTemperature: []
  });
  const [craneInfo, setCraneInfo] = useState({
    warning_temp: 70,
    critical_temp: 85
  });

  // UI State
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  // Chart refs
  const hoistChartRef = useRef();
  const travelChartRef = useRef();
  const gearboxChartRef = useRef();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  // Fetch crane list
  const fetchCraneList = useCallback(async () => {
    const token = getAuthToken();
    try {
      const url = `${BaseURL}meters/cms-cranes/`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();

      if (Array.isArray(data)) {
        setCranes(data);
      } else if (data.results && Array.isArray(data.results)) {
        setCranes(data.results);
      } else {
        setCranes([]);
      }
    } catch (error) {
      console.error('Error fetching crane data:', error);
      setCranes([]);
      showNotification('Failed to load crane data', 'error');
    }
  }, []);

  // Fetch temperature data
  const fetchTemperatureData = async (machineId) => {
    if (!machineId) {
      showNotification('Please select a crane first', 'error');
      return;
    }

    setLoading(true);
    setShowData(false);

    try {
      const response = await fetch(`${BaseURL}data/temp_monitoring-data/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken')
        },
        body: JSON.stringify({ machine_id: machineId })
      });

      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      
      setChartData({
        timestamps: data.chart_data.timestamps,
        hoistTemperature: data.chart_data.hoist_temperature,
        ctTemperature: data.chart_data.ct_temperature,
        ltTemperature: data.chart_data.lt_temperature
      });
      
      setCraneInfo(data.crane_info);
      setTableData(data.table_data);
      setShowData(true);
      showNotification('Temperature data loaded successfully', 'success');
    } catch (error) {
      console.error('Error fetching temperature data:', error);
      showNotification('Failed to load temperature data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    if (!selectedCrane) {
      showNotification('Please select a crane first', 'error');
      return;
    }
    fetchTemperatureData(selectedCrane);
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCrane('');
    setShowData(false);
    setTableData([]);
    setChartData({
      timestamps: [],
      hoistTemperature: [],
      ctTemperature: [],
      ltTemperature: []
    });
    showNotification('Filters reset', 'success');
  };

  // Get chart options with thresholds
  const getChartOptions = (warningThreshold, criticalThreshold) => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      y: {
        beginAtZero: false,
        title: { display: true, text: 'Temperature (°C)' },
        min: 0,
        max: Math.max(criticalThreshold + 10, 100)
      },
      x: {
        title: { display: true, text: 'Time of Day' }
      }
    }
  });

  // Prepare chart data for a specific component
  const prepareChartData = (labels, temperatures, componentName, color) => ({
    labels: labels,
    datasets: [
      {
        label: `${componentName} Temperature (°C)`,
        data: temperatures,
        borderColor: color,
        backgroundColor: color.replace(')', ', 0.1)').replace('rgb', 'rgba'),
        tension: 0.4,
        fill: true
      },
      {
        label: `Warning Threshold (${craneInfo.warning_temp}°C)`,
        data: Array(labels.length).fill(craneInfo.warning_temp),
        borderColor: '#f39c12',
        borderWidth: 2,
        borderDash: [6, 6],
        pointRadius: 0,
        fill: false
      },
      {
        label: `Critical Threshold (${craneInfo.critical_temp}°C)`,
        data: Array(labels.length).fill(craneInfo.critical_temp),
        borderColor: '#e74c3c',
        borderWidth: 2,
        borderDash: [6, 6],
        pointRadius: 0,
        fill: false
      }
    ]
  });

  const getStatusClass = (temperature, warningTemp, criticalTemp) => {
    if (temperature >= criticalTemp) return 'danger';
    if (temperature >= warningTemp) return 'warning';
    return 'normal';
  };

  const getStatusText = (temperature, warningTemp, criticalTemp) => {
    if (temperature >= criticalTemp) return 'Critical';
    if (temperature >= warningTemp) return 'Warning';
    return 'Normal';
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  // Load crane list on mount
  useEffect(() => {
    fetchCraneList();
  }, [fetchCraneList]);

  return (
    <div className="temp-monitoring-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
                      <div className="account-dropdown">
                        <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          {orgLogo ? (
                                            <img
                                              src={orgLogo}
                                              alt="Organization Logo"
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '2px solid #4361ee'
                                              }}
                                              onError={() => setOrgLogo(null)}
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                          )}
                          <span>{sessionStorage.getItem('username') || 'User'}</span>
                          <FontAwesomeIcon icon={faCaretDown} />
                        </div>
                        {isDropdownOpen && (
                          <div className="account-menu show">
                            <div className="account-menu-item plan-info">
                              <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                              <span>Basic Plan</span>
                            </div>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faUser} /> Profile
                            </button>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                            </button>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Temperature Monitoring</h1>
          <p>Real-time and historical temperature measurements</p>
        </div>

        {/* Filter Controls */}
        <div className="log-controls">
          <div className="filter-group">
            <label className="filter-label">Crane</label>
            <select
              className="filter-select"
              value={selectedCrane}
              onChange={(e) => setSelectedCrane(e.target.value)}
            >
              <option value="">Select a Crane</option>
              {cranes.map((crane) => (
                <option key={crane.machine_id} value={crane.machine_id}>
                  {crane.machine_name} ({crane.machine_id})
                </option>
              ))}
            </select>
          </div>

          <div className="action-buttons">
            <button className="btn btn-primary" onClick={applyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply Filters
            </button>
            <button className="btn btn-secondary" onClick={resetFilters}>
              <FontAwesomeIcon icon={faRedo} /> Reset
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="loading-indicator">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span>Loading temperature data...</span>
          </div>
        )}

        {/* Temperature Charts */}
        {showData && !loading && (
          <>
            <div className="chart-row">
              <div className="chart-containerd">
                <div className="chart-title">Hoist Motor Temperature Trend</div>
                <div className="chart-wrapper">
                  <Line
                    ref={hoistChartRef}
                    data={prepareChartData(
                      chartData.timestamps,
                      chartData.hoistTemperature,
                      'Hoist Motor',
                      '#e74c3c'
                    )}
                    options={getChartOptions(craneInfo.warning_temp, craneInfo.critical_temp)}
                  />
                </div>
              </div>

              <div className="chart-containerd">
                <div className="chart-title">CT Motor Temperature Trend</div>
                <div className="chart-wrapper">
                  <Line
                    ref={travelChartRef}
                    data={prepareChartData(
                      chartData.timestamps,
                      chartData.ctTemperature,
                      'CT Motor',
                      '#3498db'
                    )}
                    options={getChartOptions(craneInfo.warning_temp, craneInfo.critical_temp)}
                  />
                </div>
              </div>

              <div className="chart-containerd">
                <div className="chart-title">LT Motor Temperature Trend</div>
                <div className="chart-wrapper">
                  <Line
                    ref={gearboxChartRef}
                    data={prepareChartData(
                      chartData.timestamps,
                      chartData.ltTemperature,
                      'LT Motor',
                      '#2ecc71'
                    )}
                    options={getChartOptions(craneInfo.warning_temp, craneInfo.critical_temp)}
                  />
                </div>
              </div>
            </div>

            {/* Temperature Log Table */}
            <div className="log-table-container">
              <table className="log-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Crane ID</th>
                    <th>Component</th>
                    <th>Temperature (°C)</th>
                    <th>Warning Threshold</th>
                    <th>Critical Threshold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => {
                    const statusClass = getStatusClass(
                      item.temperature,
                      item.warning_temperature,
                      item.critical_temperature
                    );
                    const statusText = getStatusText(
                      item.temperature,
                      item.warning_temperature,
                      item.critical_temperature
                    );
                    const timestamp = item.timestamp.replace('T', ' ');

                    return (
                      <tr key={index}>
                        <td>{timestamp}</td>
                        <td>{item.crane_id}</td>
                        <td>{item.motor_type.toUpperCase()} Motor</td>
                        <td>{item.temperature}°C</td>
                        <td>{item.warning_temperature}°C</td>
                        <td>{item.critical_temperature}°C</td>
                        <td>
                          <span className={`temp-status status-${statusClass}`}>
                            {statusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* No Data Message */}
        {!showData && !loading && selectedCrane && (
          <div className="no-data-message">
            <p>Click "Apply Filters" to load temperature data</p>
          </div>
        )}
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <FontAwesomeIcon 
            icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} 
          />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default TemperatureMonitoring;